import { Link } from 'react-router-dom';
import { Instagram, Facebook, Linkedin, Youtube } from 'lucide-react';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const handleNavClick = (href) => {
    if (href.startsWith('#')) {
      const element = document.getElementById(href.substring(1));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <footer data-testid="footer" className="bg-[#F5F0E8] border-t border-[#C4B6A4]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_design-intent/artifacts/vquoi9vd_LC01_Trans.png"
                alt="Livingcraftsmen"
                className="h-32 w-auto"
              />
            </Link>
            <p className="text-sm text-[#3D3D3D] font-light leading-relaxed">
              Crafting timeless interiors with a focus on material, detail, and lived experience.
            </p>
          </div>

          {/* Navigation */}
          <div>
            <span className="overline text-[#2A2A2A] block mb-4">Navigate</span>
            <nav className="space-y-3">
              <Link to="/" className="block text-sm text-[#3D3D3D] hover:text-[#B5703A] transition-colors">
                Home
              </Link>
              <Link to="/portfolio" className="block text-sm text-[#3D3D3D] hover:text-[#B5703A] transition-colors">
                Portfolio
              </Link>
              <button 
                onClick={() => handleNavClick('#services')}
                className="block text-sm text-[#3D3D3D] hover:text-[#B5703A] transition-colors text-left"
              >
                Services
              </button>
              <button 
                onClick={() => handleNavClick('#about')}
                className="block text-sm text-[#3D3D3D] hover:text-[#B5703A] transition-colors text-left"
              >
                About
              </button>
            </nav>
          </div>

          {/* Services */}
          <div>
            <span className="overline text-[#2A2A2A] block mb-4">Services</span>
            <nav className="space-y-3">
              <span className="block text-sm text-[#3D3D3D]">Living Spaces</span>
              <span className="block text-sm text-[#3D3D3D]">Kitchens</span>
              <span className="block text-sm text-[#3D3D3D]">Wardrobes</span>
              <span className="block text-sm text-[#3D3D3D]">Full Home</span>
            </nav>
          </div>

          {/* Contact */}
          <div>
            <span className="overline text-[#2A2A2A] block mb-4">Contact</span>
            <div className="space-y-3">
              <a 
                href="tel:9972765135" 
                className="block text-sm text-[#3D3D3D] hover:text-[#B5703A] transition-colors"
              >
                +91 9972765135
              </a>
              <a 
                href="tel:8296031515" 
                className="block text-sm text-[#3D3D3D] hover:text-[#B5703A] transition-colors"
              >
                +91 8296031515
              </a>
              <a 
                href="mailto:connect@livingcraftsmen.com" 
                className="block text-sm text-[#3D3D3D] hover:text-[#B5703A] transition-colors"
              >
                connect@livingcraftsmen.com
              </a>
            </div>

            {/* Social Links */}
            <div className="flex flex-wrap gap-3 mt-6">
              <a 
                href="https://www.instagram.com/livingcraftsmen?igsh=MjI1YjFteXEwcW01" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 border border-[#C4B6A4] flex items-center justify-center text-[#3D3D3D] hover:border-[#B5703A] hover:text-[#B5703A] transition-colors"
                data-testid="social-instagram"
              >
                <Instagram size={18} />
              </a>
              <a 
                href="https://www.facebook.com/profile.php?id=61590244506306" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 border border-[#C4B6A4] flex items-center justify-center text-[#3D3D3D] hover:border-[#B5703A] hover:text-[#B5703A] transition-colors"
                data-testid="social-facebook"
              >
                <Facebook size={18} />
              </a>
              <a 
                href="https://www.youtube.com/@LivingCraftsmen" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 border border-[#C4B6A4] flex items-center justify-center text-[#3D3D3D] hover:border-[#B5703A] hover:text-[#B5703A] transition-colors"
                data-testid="social-youtube"
              >
                <Youtube size={18} />
              </a>
              <a 
                href="https://pin.it/7bEtp0pCl" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 border border-[#C4B6A4] flex items-center justify-center text-[#3D3D3D] hover:border-[#B5703A] hover:text-[#B5703A] transition-colors"
                data-testid="social-pinterest"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="17" x2="12" y2="22"></line>
                  <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6a3 3 0 1 0-6 0v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z"></path>
                </svg>
              </a>
              <a 
                href="https://www.linkedin.com/company/113018867" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 border border-[#C4B6A4] flex items-center justify-center text-[#3D3D3D] hover:border-[#B5703A] hover:text-[#B5703A] transition-colors"
                data-testid="social-linkedin"
              >
                <Linkedin size={18} />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-[#C4B6A4] mt-12 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <span className="text-xs text-[#3D3D3D]">
            © {currentYear} Livingcraftsmen. All rights reserved.
          </span>
          <div className="flex items-center gap-6">
            <Link 
              to="/privacy-policy" 
              className="text-xs text-[#3D3D3D] hover:text-[#B5703A] transition-colors"
              data-testid="privacy-policy-link"
            >
              Privacy Policy
            </Link>
            <span className="text-xs text-[#3D3D3D]">
              Crafting spaces with intention.
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
